/* Things growing on the ground, and things living in it.
 *
 * WHY THIS EXISTS. Every screen was tiles and obstacles and nothing else: a
 * reviewer's top-ranked art defect two rounds running was that the wilds read
 * as "a walled rectangle full of rectangles", and the honest reason is that
 * nothing on the floor was smaller than a tile. A hedge is 16 pixels. A tuft
 * of grass is four, and four-pixel things are what makes a place look lived
 * in rather than drawn.
 *
 * TWO RULES THIS LAYER LIVES UNDER.
 *
 *   1. IT NEVER BLOCKS. Not one of these is solid, and none is consulted by
 *      `passable.js`. The gating proof runs over 300 seeds and must not know
 *      this file exists — decoration that can wall you in is a bug with a
 *      flower painted on it.
 *   2. IT IS DETERMINISTIC. Placed from the screen's own seed, so the same
 *      world grows the same grass, and the replay checks still hold.
 *
 * WHAT MAKES IT INTERACTIVE. Grass you can walk through is only worth having
 * if it KNOWS you walked through it: each tuft carries a bend, set by anything
 * that overlaps it and decaying on its own, and the critters scatter when
 * something comes near. Nothing here changes what you can do — it changes
 * whether the ground answers you.
 */
import { RNG, mix } from './rng.ts';
import { T, TS, UI } from './art.ts';
import * as M3 from './model3d.ts';
import { mixHex } from './grass.ts';

/** The kinds, as 8x8 row masks. `#` is the body colour, `o` the shade, `:` a
 *  highlight. Four rows of ground clutter and two things that move. */
export const DECALS = {
  // --- grass: three heights, so a meadow is not a stamp repeated
  tuft: { h: 6, bend: 1.0, mask: [
    '........', '........', '...#....', '..#.#..#', '.#..#.#.', '#...#.#.',
    '....#.#.', '....#...'] },
  blade: { h: 8, bend: 1.0, mask: [
    '....#...', '...#....', '...#....', '..#.#...', '..#.#..#', '.#..#.#.',
    '.#..#.#.', '.#..#.#.'] },
  // (There was a `reed` here — two vertical strokes — and it read as a
  // scratch on the ground for exactly the reason the tufts did: at this scale
  // an isolated blade is two lit pixels and two lit pixels are a speck, not a
  // plant. Anything blade-shaped belongs to the turf layer, which draws it as
  // part of a mass. What is left in this file is things that really are
  // discrete objects.)
  // --- flowers and fungus: colour, not motion
  /* A STAND OF REEDS at the water's edge (LS-244). The mask is what the placer measures (height and footprint); the
   * picture is built as solids in `solidScene`. */
  reed: { h: 11, bend: 1.0, mask: [
    '..#..#..', '..#..#..', '.#...#.#', '.#...#.#', '.#..#..#', '.#..#..#',
    '..#.#..#', '..#.#..#'] },
  bloom: { h: 5, bend: 0.5, mask: [
    '........', '........', '........', '.:.:....', '..#.....', '..#.....',
    '..#..:..', '..#.....'] },
  cap: { h: 4, bend: 0.2, mask: [
    '........', '........', '........', '........', '..:::...', '.:###:..',
    '...#....', '...#....'] },
  /* --- GRIT: what a stone floor is covered in.
   *
   * Three or four loose chips rather than one shape, because the fault it is
   * there to fix is a 67%-of-the-screen expanse of identical floor tile, and
   * one more repeated stamp would be the same fault at a smaller scale. No
   * bend: it is not rooted in anything, it is lying on the ground. */
  /* Two chips with an edge each, NOT four loose pixels.
   *
   * The first version of this was two `#` and two `:` on their own, and an
   * art reviewer read the result off a rendered frame as "a sparse drizzle of
   * isolated single pixels — visually indistinguishable from dither noise or
   * a dead-pixel screen", in a room carrying 41 of them. Half of that was the
   * ink (`:` resolved to `floor2`, eleven units from the floor it lay on) and
   * half was the shape: a lone pixel has no silhouette, and silhouette is the
   * only thing that reads at this size. Outdoors a tuft is a 4-5px blade with
   * a lit tip, which is why the overworld has texture and this did not.
   *
   * Each chip is now a lit core inside a darker rim — the same construction
   * as `pebble`, at a third the size and twice per stamp, so grit reads as
   * several small pieces where a pebble reads as one stone. */
  grit: { h: 2, bend: 0, mask: [
    '........', '........', '........', '.oo.....',
    '.o#o..oo', '..o..o#o', '......oo', '........'] },
  // --- litter: stones, bones, fallen leaves
  pebble: { h: 3, bend: 0, mask: [
    '........', '........', '........', '........', '........', '..oo....',
    '.o##o...', '..oo....'] },
  bone: { h: 3, bend: 0, mask: [
    '........', '........', '........', '........', '.o..o...', 'o##o#o..',
    '.o..o...', '........'] },
  leafmeal: { h: 2, bend: 0.3, mask: [
    '........', '........', '........', '........', '........', '..o..o..',
    '.o#o.#o.', '..o..o..'] },
};

/** Which decals grow in which biome, and how thickly. A meadow is not a
 *  gravel bar, and reading the ground should tell you where you are before
 *  the place name does. */
// GRASS IS NOT IN THIS LIST ANY MORE. Scattering individual eight-pixel tufts
// gave the right distribution and the wrong picture: at this scale one blade
// is three or four lit pixels, and a field of isolated three-pixel marks reads
// as dirt with specks on it rather than as grass. Grass is a MASS, so it moved
// to `src/grass.ts` as a coverage layer with merging patches. What is left
// here is the things that really are individual objects.
export const FLORA = {
  heath: { kinds: ['bloom', 'pebble', 'leafmeal'], density: 0.16, critters: 'moth' },
  /* A MUSHROOM IS A FIND TOO. `cap` is drawn in `UI.spore`, which is very
   * nearly white, and it was one kind in three in bloom and one in TWO in fen
   * -- fen at the highest density in the game. On the whole island both
   * countries came out sprinkled with white dots on an even spacing, which is
   * the same fault the note below records for bone: a bright mark used as a
   * texture rather than as an event. One in five, and fen's density comes back
   * toward the others. */
  bloom: { kinds: ['bloom', 'bloom', 'bloom', 'leafmeal', 'cap'],
           density: 0.20, critters: 'moth' },
  fen:   { kinds: ['leafmeal', 'bloom', 'leafmeal', 'bloom', 'cap'],
           density: 0.22, critters: 'gnat' },
  /* A BONE IS A FIND, OUTDOORS AS WELL AS IN.
   *
   * The note below says it for the hollows -- `bone` is drawn in `UI.bone`,
   * which is near white, so on a dark floor it is a row of bright dots -- and
   * the same argument was never carried outside. One kind in three was bone in
   * all three dry countries, and the scree plateau is fifteen screens of pale
   * mauve: at map scale it came out speckled with white all over, which reads
   * as litter scattered on the ground rather than as ground.
   *
   * NONE, in the end. One in six still left the plateau speckled -- the tone is
   * the problem, not the count, and `UI.bone` against ash's lavender is as
   * bright a mark as the game has. It stays where the note below puts it: a
   * lair, where a bright dot is earned. Stone carries the outdoor litter, in
   * the country's own `bed` tones. The density is down as well,
   * because the floor of a dry country grew a texture of its own this round
   * (see `grit` in `art.ts`) and the scatter layer no longer has to be what
   * stops it being flat. */
  // the grit and most of the pebbles moved to `soil.ts`, which lays them in
  // world space as shapes; the decal layer keeps a leaf and the odd stone
  dust:  { kinds: ['pebble', 'pebble', 'pebble', 'leafmeal'],
           density: 0.12, critters: 'beetle' },
  scree: { kinds: ['pebble', 'grit'],
           density: 0.12, critters: 'beetle' },
  /* AND NOTHING FRUITS IN THE ASH. `cap` is a mushroom drawn in the spore
   * violet, near white against ash's lavender ground, and it was one kind in
   * three here: a burnt waste came out dotted with toadstools, and at map scale
   * that is what the pale speckling over the whole plateau was. */
  ash:   { kinds: ['pebble'],
           density: 0.10, critters: 'beetle' },
};

/** Ground a thing can grow out of. Deliberately not `walkableTile`: this is a
 *  question about SOIL, not about passage, and tying the two together is how a
 *  decoration layer ends up deciding where a player may walk. */
const SOIL = { tuft: [T.GROUND, T.MOSS], blade: [T.GROUND, T.MOSS],
  bloom: [T.GROUND, T.MOSS],
  cap: [T.MOSS, T.GROUND],
  // ...and stone lies on a stone floor. This is the line that was missing:
  // `growDecals` ran in every hollow and rejected every dart for want of
  // soil, which is why the interiors had no scatter layer at all.
  pebble: [T.GROUND, T.SAND, T.SCORCH, T.DEBRIS],
  bone: [T.SAND, T.GROUND, T.SCORCH],
  // ...and outdoors too. Chips of stone lie on any ground, and the dry
  // countries need something mineral to scatter that is not near-white bone.
  grit: [T.SCORCH, T.DEBRIS, T.GROUND, T.SAND, T.BONES],
  reed: [T.GROUND, T.MOSS, T.SAND],   // reeds root on the bank, never in the water itself (LS-244)
  leafmeal: [T.GROUND, T.MOSS, T.SAND] };

/** One patch of ground clutter. */
class Decal {
  bend: any;
  dir: any;
  flip: any;
  kind: any;
  x: any;
  y: any;

  /* `v` IS WHY TWO OF A KIND ARE NOT THE SAME OBJECT.
   *
   * A decal was a kind, a place and a horizontal flip, and the flip does
   * nothing at all to a symmetrical stamp: measured over three worlds, 4,625
   * of 5,462 decals — 85% — were pixel-identical to another one ON THE SAME
   * SCREEN, and the median screen repeated a single stamp NINE times, worst 25.
   * An art critic reading one playfield found the same ring of dots four times
   * over and called it screen dirt rather than a plant, which is exactly what a
   * stamp repeated at that rate stops being.
   *
   * The variant is taken from the decal's WORLD position, so a plant is the
   * same plant whenever you walk back to it, and it drives two things in
   * `drawDecal`: which of the head's pixels is missing, and a small shift of
   * tone. Neither invents new art — it is the same mask and the same three
   * inks — and between them no two of a kind on a screen come out the same. */
  v: any;

  constructor(kind, x, y, flip, v = 0) {
    this.kind = kind; this.x = x; this.y = y; this.flip = flip;
    this.v = v;
    this.bend = 0;          // 0..1, how far it is pushed over right now
    this.dir = 1;           // which way it was pushed
  }
}

/** Something small and alive. It does nothing to you and you can do nothing to
 *  it; it moves when you come near, which is the entire point. */
class Critter {
  kind: any;
  x: any;
  y: any;

  hx: any;
  hy: any;
  rng: any;
  startled: any;
  t: any;

  constructor(kind, x, y, rng) {
    this.kind = kind; this.x = x; this.y = y;
    this.hx = x; this.hy = y;                 // where it wants to be
    this.t = rng.int(120);
    this.startled = 0;
    this.rng = rng;
  }
}

/* PLACEMENT: A DENSITY MAP AND A DART THROW, NOT A DICE ROLL PER TILE.
 *
 * The first version rolled three dice per tile. Two things are wrong with that
 * and both are visible: the grid shows, because every clump is bounded by tile
 * edges, and the distribution is Poisson, which means it clumps into blotches
 * and leaves bald patches at the same time. Scattering by uniform random is
 * the classic way to make a computer-generated field look computer-generated.
 *
 * Two techniques instead.
 *
 *   A DENSITY MAP. A low-frequency noise field over WORLD space says how thick
 *   the growth is here — so a meadow is a meadow across several screens, a
 *   gravel bar is bare across several screens, and the density varies within a
 *   screen instead of being one number for all of it. World space, so it runs
 *   across seams like the terrain does.
 *
 *   BLUE NOISE, by dart throwing. Candidates are rejected if they land within
 *   a minimum distance of something already placed. That is Mitchell's best-
 *   candidate in its cheapest form and it is what turns a blotchy scatter into
 *   an even one that is still irregular — the distribution a handful of seeds
 *   thrown on soil actually has.
 */

/** Value noise on the same lattice the world uses, so the density map is
 *  continuous across screen seams. */
/* TWO OCTAVES. One cell of 28px varied the ground within a screen and
 * averaged out across it: on identical soil, screens forty tiles apart grew
 * 49 to 71 decals against a bar of 1.6x (thick country and bare country
 * are what the map is for). A second, coarse octave of about a screen and
 * a half carries the region, the fine one the patchiness within it. */
function dmapAt(seed, x, y, s) {
  const ix = Math.floor(x * s), iy = Math.floor(y * s);
  const fx = x * s - ix, fy = y * s - iy;
  const sm = (t) => t * t * (3 - 2 * t);
  const at = (a, b) => ((mix(seed, a + 1013, b + 2027) >>> 8) / 16777216);
  const u = sm(fx), v = sm(fy);
  const a = at(ix, iy), b = at(ix + 1, iy), c = at(ix, iy + 1), d = at(ix + 1, iy + 1);
  return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
}
function dmap(seed, x, y) {
  const fine = dmapAt(seed, x, y, 0.035);
  const coarse = dmapAt(seed ^ 0x2b7e, x, y, 0.0026);
  return Math.max(0, Math.min(1, fine * 0.45 + (coarse * 1.3 - 0.15) * 0.55));
}

/** Grow a screen's worth, from its own seed. */
/* WHAT GROWS HERE IS A FACT ABOUT HERE, NOT ABOUT THE SCREEN.
 *
 * The ground palette has been asked per TILE since the biome boundary stopped
 * being a screen boundary, and the turf's cover followed it for the same
 * reason. The scatter layer never did: it read ONE flora key for the whole
 * screen, from the biome at the screen's centre.
 *
 * Measured on the finished map, screen (2,4) reports `palKey` bloom and places
 * 21 mushrooms and 26 flowers -- while most of that screen's ground is drawn in
 * ash's lavender, because the blend there runs ash-to-bloom. So a burnt waste
 * came out dotted with toadstools, and at map scale the whole plateau was
 * speckled pale. I chased that speckle through the canopy, the turf, the bone
 * tone and ash's own kind list before looking at which key the screen actually
 * used; three of those four passes changed nothing, because the caps were being
 * asked for by the wrong country.
 *
 * `floraAt` is the same shape as the turf's `coverAt`: the world position
 * decides. */
export function growDecals(tiles, biome, seed, cols, rows, ox = 0, oy = 0,
  floraAt = null) {
  const flora = FLORA[biome] || FLORA.heath;
  const rng = new RNG(mix(seed, 60013));
  const out = [];
  const passed: { x: number; y: number }[] = [];   // every dart that passed the density test, kept or not: the spacing is judged against these (2026-09-18)
  const W = cols * TS, H = rows * TS;
  /* Darts are thrown for the THICKEST country and thinned per dart by the local
   * one, so a screen straddling two countries gets each one's density where it
   * belongs. For a screen wholly inside one country the expected count is what
   * it always was: darts scale by TOP and the acceptance by density/TOP. */
  const TOP = 0.26;
  const darts = Math.round(W * H * TOP / 90);
  const MIN = 7;                       // no two closer than this, in pixels
  const MIN2 = MIN * MIN;
  for (let i = 0; i < darts; i++) {
    const x = rng.int(W - 8), y = rng.int(H - 8);
    const tx = Math.floor((x + 4) / TS), ty = Math.floor((y + 4) / TS);
    if (tx < 0 || ty < 0 || tx >= cols || ty >= rows) continue;
    // thick here, or bare here?
    const d = dmap(seed ^ 0x5f3a, ox + x, oy + y);
    const fl = floraAt
      ? (FLORA[floraAt(Math.floor((ox + x) / TS), Math.floor((oy + y) / TS))] || flora)
      : flora;
    if (!rng.chance((0.25 + d * 1.1) * (fl.density / TOP))) continue;
    /* THE SPACING IS JUDGED AGAINST EVERY DART THAT PASSED THE DENSITY, NOT AGAINST WHAT ROOTED (2026-09-18). The
     * blue-noise test below compared each dart with the plants ALREADY KEPT, and whether a dart is kept depends on
     * the tile under it -- so cutting or burning one tile of grass changed which darts rooted, which changed what
     * every later dart on the screen was measured against, and plants appeared and vanished all over it. Measured on
     * one seed: 44 plants moved elsewhere on their screen after a single tile became straw, twelve of them on one
     * screen of forty-three. Judged against the darts, the kept set is a subset of a tile-independent pattern, so a
     * tile that changes can only add or remove the plants standing on it. */
    passed.push({ x, y });
    /* ...AND EVERY DART DRAWS THE SAME NUMBER OF TIMES (2026-09-18). The rolls below were made only when the tiles
     * asked for them -- the reed's roll only on a bank, the flip only for a dart that was kept -- so a tile that
     * changed shifted the RNG stream for every later dart on the screen. The three rolls are made here, for every
     * dart alike, and used further down. */
    const kind0 = rng.pick(fl.kinds);
    const reedRoll = rng.chance(0.8);
    const flip = rng.chance(0.5);
    let kind = kind0;
    /* REEDS GROW WHERE THE WATER IS (LS-244). A dart that lands on the bank -- soil with water within a tile -- grows
     * a stand of reeds instead of whatever the country would have put there, so a shore reads as a shore. */
    { let wet = false;
      for (let dy = -1; dy <= 1 && !wet; dy++) for (let dx = -1; dx <= 1; dx++) {
        const nx = tx + dx, ny = ty + dy; if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
        const v2 = tiles[ny][nx];
        if (v2 === T.WATER || v2 === T.SHALLOW || v2 === T.SEA) { wet = true; break; }
      }
      if (wet && reedRoll) kind = 'reed'; }
    // THE WHOLE FOOTPRINT HAS TO BE SOIL, not just the middle of it. A decal
    // is 8px in a 16px tile, so one placed near a tile edge overhangs its
    // neighbour — measured, 33 of 2029 had part of themselves growing out of a
    // rock. It cannot block anything either way; it just looked wrong.
    const soil = SOIL[kind];
    let rooted = true;
    for (const [ex, ey] of [[x, y], [x + 7, y], [x, y + 7], [x + 7, y + 7]]) {
      const cx = Math.floor(ex / TS), cy = Math.floor(ey / TS);
      if (cx < 0 || cy < 0 || cx >= cols || cy >= rows || !soil.includes(tiles[cy][cx])) {
        rooted = false; break;
      }
    }
    if (!rooted) continue;
    // blue noise: reject anything landing on top of a dart that came before it (the darts, not the plants)
    let clash = false;
    for (const o of passed) {
      if (o.x === x && o.y === y) continue;   // itself
      const dx = o.x - x, dy = o.y - y;
      if (dx * dx + dy * dy < MIN2) { clash = true; break; }
    }
    if (clash) continue;
    // the variant rides on the WORLD position, not the dart order, so walking
    // away and back finds the same plant rather than a reshuffled one
    out.push(new Decal(kind, x, y, flip,
      (mix(seed ^ 0x2b91, ox + x, oy + y) >>> 7) & 31));
  }
  /* ...AND THE BANK IS PLANTED, not just sprinkled (LS-244). The country's darts are thrown over the whole screen, so
   * a shore got a stand or two by chance; every second bank tile grows one now, which is what makes a fen edge read as
   * a fen edge rather than as ground that happens to stop. */
  for (let ty = 0; ty < rows; ty++) for (let tx = 0; tx < cols; tx++) {
    const v = tiles[ty][tx];
    if (!SOIL.reed.includes(v)) continue;
    let wet = false;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) { const nx = tx + dx, ny = ty + dy; if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue; const n = tiles[ny][nx]; if (n === T.WATER || n === T.SHALLOW || n === T.SEA) wet = true; }
    if (!wet) continue;
    const h = mix(seed ^ 0x71ed, ox + tx, oy + ty) >>> 0;
    if ((h % 3) !== 0) continue;   // one bank in three (LS-394, 2026-09-12; Vijay: "those green assets along the water look horrible" -- three in four was a fence of stands the length of every shore)
    /* AN UNSIGNED SHIFT (2026-09-18). `h` is a mix, and half of its values are negative: `(h >> 4) % 8` is then a
     * NEGATIVE remainder, so half the bank reeds were planted seven pixels west and six north of the tile they were
     * chosen for -- which put a measured twelve stands of them rooted in open water on one coast. */
    const x = tx * TS + 1 + (h >>> 4) % 8, y = ty * TS + 2 + (h >>> 8) % 7;
    let clash = false;
    for (const o of out) { const dx = o.x - x, dy = o.y - y; if (dx * dx + dy * dy < 25) { clash = true; break; } }
    if (clash) continue;
    out.push(new Decal('reed', x, y, ((h >> 12) & 1) === 1, (h >> 13) & 31));
  }
  return out;
}

/** ...and the things living in it. Few, and never on a wall. */
export function growCritters(tiles, biome, seed, cols, rows) {
  const flora = FLORA[biome] || FLORA.heath;
  const rng = new RNG(mix(seed, 60017));
  const out = [];
  const want = 2 + rng.int(3);
  for (let i = 0; i < want * 6 && out.length < want; i++) {
    const tx = 1 + rng.int(cols - 2), ty = 1 + rng.int(rows - 2);
    const t = tiles[ty][tx];
    if (t !== T.GROUND && t !== T.MOSS && t !== T.SAND) continue;
    out.push(new Critter(flora.critters, tx * TS + 4 + rng.int(8),
      ty * TS + 4 + rng.int(8), new RNG(mix(seed, i, 991))));
  }
  return out;
}

/** Push anything overlapping `box` aside, and let the rest stand back up.
 *  Called once a frame with every body that can disturb the ground. */
export function stirDecals(decals, bodies) {
  for (const d of decals) {
    const def = DECALS[d.kind];
    if (def.bend > 0) {
      let touched = null;
      for (const b of bodies) {
        if (b.x < d.x + 8 && b.x + b.w > d.x && b.y < d.y + 8 && b.y + b.h > d.y) {
          touched = b; break;
        }
      }
      if (touched) {
        d.bend = def.bend;
        d.dir = touched.cx > d.x + 4 ? -1 : 1;
      } else if (d.bend > 0) {
        d.bend = Math.max(0, d.bend - 0.055);
      }
    }
  }
}

/** Move the wildlife. `near` is whatever it should be frightened of. */
export function stirCritters(critters, near, cols, rows) {
  for (const c of critters) {
    c.t++;
    const d = Math.hypot(near.cx - c.x, near.cy - c.y);
    if (d < 26 && c.startled <= 0) {
      c.startled = 34;
      const a = Math.atan2(c.y - near.cy, c.x - near.cx);
      c.hx = Math.max(6, Math.min(cols * TS - 6, c.x + Math.cos(a) * 26));
      c.hy = Math.max(6, Math.min(rows * TS - 6, c.y + Math.sin(a) * 26));
    }
    if (c.startled > 0) c.startled--;
    // always drifting a little, faster when startled
    const speed = c.startled > 0 ? 0.9 : 0.12;
    c.x += Math.max(-speed, Math.min(speed, c.hx - c.x));
    c.y += Math.max(-speed, Math.min(speed, c.hy - c.y));
    if (c.startled <= 0 && c.t % 90 === 0) {
      c.hx = Math.max(6, Math.min(cols * TS - 6, c.x + c.rng.range(-14, 14)));
      c.hy = Math.max(6, Math.min(rows * TS - 6, c.y + c.rng.range(-10, 10)));
    }
  }
}

/** The colours a decal is painted in, per biome, so the same grass is a
 *  different green in the fen and the heath.
 *
 *  EXPORTED so the suite can ask it what a decal is actually painted in. A
 *  check that read the palette instead — `pal.wall` against `pal.floor` —
 *  passed happily when this function was mutated to return the floor colour,
 *  which is the exact fault it was written to catch: grit invisible on the
 *  ground it lies on. Test the chooser, not the box it chooses from. */
export function inks(kind, pal) {
  if (kind === 'pebble') return [pal.bed2 || UI.slate, pal.bed || UI.slate, UI.paperDim];
  if (kind === 'bone') return [UI.bone || UI.paperDim, UI.muted, UI.paper];
  // grit takes the room's own stone, so the three hollow palettes stay three
  // hollow palettes instead of gaining one shared grey
  // ...and the core is the WALL tone, which is the lightest stone the room
  // owns. `wall2` on `floor` was 54 units apart and the `:` pixels resolved to
  // `floor2`, which is eleven — so most of the stamp was painted in the floor
  // it was lying on. Light core, darker rim, both from the room's own palette
  // so the three hollows stay three hollows.
  if (kind === 'grit') return [pal.wall || pal.bed || UI.slate,
    pal.wall2 || pal.bed2 || UI.muted, pal.wall || UI.paperDim];
  /* A REED IS SEDGE, NOT LAWN (LS-244): the country's green pulled a third toward its own sand, so a stand on a pale
   * shore is olive rather than a row of bright green bars, with a straw-lit side. */
  if (kind === 'reed') { const g0 = pal.grass2 || UI.leaf; return [mixHex(g0, pal.sand || UI.linen, 0.34), mixHex(pal.grass || UI.leafDim, pal.shade || UI.ink, 0.3), mixHex(pal.sand || UI.linen, UI.paper, 0.25)]; }
  if (kind === 'cap') return [UI.spore || UI.violetLit, UI.violet, UI.paperDim];
  if (kind === 'bloom') return [pal.grass2 || UI.leaf, UI.leafDim, UI.gold];
  if (kind === 'leafmeal') return [pal.grass2 || UI.leafDim, UI.leafDim, UI.paperDim];
  return [pal.grass2 || UI.leaf, pal.grass || UI.leafDim, UI.leafLit || UI.paperDim];
}

/** Draw one, leaning by its bend. The lean is applied per ROW — the further
 *  from the ground, the further it is pushed — so grass bends rather than
 *  sliding sideways, which is what tells you it is rooted. */

/* THE PLANTS ARE SOLIDS NOW (LS-244; Vijay: "the reeds object needs a visual upgrade i think it's still from the pre
 * 3d pipeline era"). A flower, a mushroom and a stand of reeds were 8x8 bitmasks from before `model3d.ts` existed:
 * flat, unlit, two pixels wide, and beside a boar and a thorn hedge that are both rendered as solids they read as
 * scratches on the ground. They are built out of rods and balls through the same one camera and one light as
 * everything else, with a contact shadow under them so they stand on the ground rather than float over it.
 *
 * The picture is cached by (kind, variant, palette, bend step): a screen of forty plants is forty blits, and the bend
 * is quantised to five steps so walking through a stand redraws each plant at most four times. */
const SOLIDS = new Set(['bloom', 'cap', 'reed']);
const solidCache = new Map();
const CELL = 18;   // the cell a solid decal is rendered in: taller than the 8x8 masks, so a reed can be a reed

function solidScene(kind, v, body, shade, lit, lean) {
  const r = new RNG(mix(60017, v, kind.length));
  const skin = { base: body, lit, dark: shade };
  const out = [];
  if (kind === 'reed') {
    /* A STAND OF REEDS: four to six stalks of different heights out of one root, each leaning with the wind, the
     * tallest carrying a dark seed head. This is the shape a fen edge wants and the shape the old two-stroke mask
     * was reaching for. */
    /* A FAN, NOT A PICKET (LS-394): five to eight stalks 1.7 px apart at near one height were a rectangle of green
     * with an outline round it. Three to five stalks, wider apart, the middle ones tallest and the outer ones
     * leaning outward: a clump with a silhouette. */
    const n = 3 + r.int(3);
    for (let i = 0; i < n; i++) {
      const off = i - (n - 1) / 2;
      const x0 = 6 + off * 2.3 + (r.int(3) - 1) * 0.5;
      const h = 7 + r.int(4) + Math.max(0, 3.5 - Math.abs(off) * 1.8);
      const own = (r.int(5) - 2) * 0.35 + off * 0.55;                 // each stalk leans its own way as well as the wind's
      const mid = h * 0.55, mx = x0 + (lean + own) * (mid / 12), tip = x0 + (lean + own) * (h / 7);
      const z = (r.int(3) - 1) * 0.5;
      // TWO SEGMENTS, TAPERING. One rod at one radius is a bar; a thicker foot and a thinner head is a stalk, and the
      // upper segment leans further, so the stand arches instead of standing to attention.
      out.push({ kind: 'rod', a: [x0, 0, 0], b: [mx, mid, z * 0.5], r: 0.55 + r.int(2) * 0.1, skin });
      out.push({ kind: 'rod', a: [mx, mid, z * 0.5], b: [tip, h, z], r: 0.36, skin });
      if (h >= 12) out.push({ kind: 'ball', at: [tip, h + 0.6, z], r: 0.55, ry: 1.5, rz: 0.55, skin: { base: shade, lit: body, dark: shade } });   // a seed head on the tallest
    }
    return out;
  }
  if (kind === 'cap') {
    // A MUSHROOM: a stalk, a dome with a lit crown, and a gill line under it
    const h = 3 + r.int(2);
    out.push({ kind: 'rod', a: [6, 0, 0], b: [6 + lean * 0.3, h, 0], r: 0.9, skin: { base: shade, lit: body, dark: shade } });
    out.push({ kind: 'ball', at: [6 + lean * 0.3, h + 0.9, 0], r: 3.1, ry: 1.9, rz: 2.6, skin });
    out.push({ kind: 'ball', at: [5.2 + lean * 0.3, h + 1.6, 0.8], r: 1.0, ry: 0.6, rz: 0.8, skin: { base: lit } });
    return out;
  }
  // A FLOWER: a stem, two leaves and a head of three petals round a bright eye
  const h = 5 + r.int(3);
  const top = [6 + lean * (h / 10), h, 0];
  out.push({ kind: 'rod', a: [6, 0, 0], b: top, r: 0.5, skin: { base: shade, lit: body, dark: shade } });
  out.push({ kind: 'rod', a: [6 + lean * 0.2, h * 0.45, 0], b: [3.6 + lean * 0.2, h * 0.6, 0.3], r: 0.5, skin: { base: shade, lit: body, dark: shade } });
  out.push({ kind: 'rod', a: [6 + lean * 0.3, h * 0.6, 0], b: [8.4 + lean * 0.3, h * 0.75, -0.3], r: 0.5, skin: { base: shade, lit: body, dark: shade } });
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + (v & 3) * 0.3;
    out.push({ kind: 'ball', at: [top[0] + Math.cos(a) * 1.2, top[1] + 0.6 + Math.sin(a) * 0.7, Math.sin(a) * 1.0], r: 1.1, ry: 0.9, rz: 1.0, skin });
  }
  out.push({ kind: 'ball', at: [top[0], top[1] + 0.9, 0.6], r: 0.7, skin: { base: lit } });
  return out;
}

function solidSheet(kind, v, body, shade, lit, bendStep) {
  const key = kind + '|' + v + '|' + body + '|' + shade + '|' + lit + '|' + bendStep;
  let hit = solidCache.get(key);
  if (hit) return hit;
  const lean = bendStep * 0.9;
  // the outline is the plant's own dark, not the near-black every solid uses: at a one-pixel stalk a hard outline IS the stalk
  hit = M3.renderScene(CELL, CELL, solidScene(kind, v, body, shade, lit, lean), 0, CELL - 1, kind === 'reed' ? mixHex(shade, '#241a24', 0.2) : '#241a24');   // a reed's edge is barely darker than its shade: the outline was the picket's fence (LS-394)
  if (solidCache.size > 900) solidCache.clear();   // a screen is at most a few dozen; this only grows across many screens
  solidCache.set(key, hit);
  return hit;
}

export function drawDecal(g, d, pal) {
  const def = DECALS[d.kind];
  let [body, shade, lit] = inks(d.kind, pal);
  const v = d.v | 0;
  /* A SHADE OF ITS OWN. Two steps either way, mixed toward the shade the kind
   * already uses, so a stand of the same plant has depth in it instead of
   * being one colour stamped repeatedly. It never leaves the kind's own three
   * inks, so a mushroom is still that mushroom's violet. */
  const tone = ((v & 7) - 3.5) * 0.075;
  if (tone > 0) body = mixHex(body, lit, tone);
  else if (tone < 0) body = mixHex(body, shade, -tone);
  /* ...AND ONE PIXEL OF THE HEAD IS MISSING, which of them depending on the
   * variant. At eight pixels across, one lit cell is the difference between
   * two plants and one plant drawn twice — and it is taken from the TOP half
   * only, where a gap reads as a leaf turned away rather than as a broken
   * stem. Kinds too sparse to lose one keep all of theirs. */
  if (SOLIDS.has(d.kind)) {
    // the solid plants: a picture through the one camera, a contact shadow under it, blitted
    const bendStep = Math.max(-2, Math.min(2, Math.round(d.bend * d.dir * 2)));
    const sheet = solidSheet(d.kind, v & 15, body, shade, lit, bendStep);
    const x0 = d.x + 4 - CELL / 2, y0 = d.y + 8 - CELL;   // the cell's foot sits on the decal's own base row
    M3.groundShadow([6, 0, 1], 2.6, x0 + CELL / 2 - 4, d.y + 5, (px2, py, c) => { g.fillStyle = c; g.fillRect(px2, py, 1, 1); }, '#00000030');
    M3.blitRuns(sheet, (px2, py, w, c) => { g.fillStyle = c; g.fillRect(x0 + px2, y0 + py, w, 1); });
    return;
  }
  let head = 0;
  for (let y = 0; y < 4; y++) for (let x = 0; x < 8; x++) if (def.mask[y][x] !== '.') head++;
  const drop = head >= 3 ? (v >> 3) % head : -1;
  let seenHead = 0;
  for (let y = 0; y < 8; y++) {
    const row = def.mask[y];
    // 0 at the base, 1 at the tip
    const up = (7 - y) / 7;
    const lean = Math.round(d.bend * d.dir * up * 3);
    for (let x = 0; x < 8; x++) {
      const ch = row[d.flip ? 7 - x : x];
      if (ch === '.') continue;
      if (y < 4) { if (seenHead++ === drop) continue; }
      g.fillStyle = ch === '#' ? body : ch === 'o' ? shade : lit;
      g.fillRect(d.x + x + lean, d.y + y, 1, 1);
    }
  }
}

/** Draw a critter: two pixels and a wing, which is as much as anything this
 *  small needs. */
export function drawCritter(g, c, frame) {
  const flap = Math.floor(frame / 4) % 2;
  const col = c.kind === 'moth' ? UI.paperDim : c.kind === 'gnat' ? UI.mutedLit : UI.bark;
  g.fillStyle = col;
  const x = Math.round(c.x), y = Math.round(c.y);
  g.fillRect(x, y, 1, 1);
  if (c.kind === 'beetle') { g.fillRect(x + 1, y, 1, 1); return; }
  if (flap || c.startled > 0) { g.fillRect(x - 1, y - 1, 1, 1); g.fillRect(x + 1, y - 1, 1, 1); }
  else { g.fillRect(x - 1, y, 1, 1); g.fillRect(x + 1, y, 1, 1); }
}
